import React, { useEffect, useState } from 'react';

import T from 'utils/useLocalizer';
import FormLabel from 'components/form/label/FormLabel';
import { TableSkeleton } from 'components/ui/skeleton/skeleton';
import { Plus } from 'react-bootstrap-icons';
import { IOrderCustomStatusFormData, IOrderSettingsFormData, ORDER_CUSTOM_STATUS_SORT } from 'models/ordersModel';
import { ListHeaderItem } from 'models/commonModel';
import { common } from 'utils/resources';
import Error from 'components/ui/error/Error';
import EmptyState from 'components/ui/emptyState/EmptyState';
import CustomStatusListItem from './CustomStatusListItem';
import CustomStatusListHeader from '../orderSettings/CustomStatusListHeader';
import AddCustomStatusModal from './AddCustomStatusModal';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useLanguage } from 'utils/useLanguage';

export interface Props {
  customStatusList: IOrderCustomStatusFormData[];
  isListLoading?: boolean;
  isError?: boolean;
}

const CustomStatusForm = ({ customStatusList, isListLoading, isError }: Props) => {
  const { language } = useLanguage();
  const { control } = useFormContext<IOrderSettingsFormData>();
  const [customStatuses, setCustomStatuses] = useState<IOrderCustomStatusFormData[]>([]);
  const [showAddModal, setShowAddModal] = useState(false);

  const { fields, append, update, remove } = useFieldArray({
    name: 'customStatuses',
    control,
  });

  const resources = {
    customStatusLabel: T('orders.order.settings.custom.status.label'),
    customStatusLabelDesc: T('orders.order.settings.custom.status.label.description'),
    addCustomStatusLabel: T('orders.order.settings.custom.status.add.custom.status.label'),
    statusLabel: T('orders.order.settings.custom.status.table.status.label'),
    notifyLabel: T('orders.order.settings.custom.status.table.notify.label'),
    templateLabel: T('orders.order.settings.custom.status.table.template.label'),
    noDataTitle: T(common.noData),
  };

  const headerItems: ListHeaderItem<ORDER_CUSTOM_STATUS_SORT>[] = [
    {
      id: ORDER_CUSTOM_STATUS_SORT.STATUS,
      label: resources.statusLabel,
      // sortable: true
    },
    {
      id: ORDER_CUSTOM_STATUS_SORT.NOTIFY,
      label: resources.notifyLabel,
      sortable: true,
    },
    {
      id: ORDER_CUSTOM_STATUS_SORT.TEMPLATE,
      label: resources.templateLabel,
      sortable: true,
    },
  ];

  useEffect(() => {
    setCustomStatuses(
      customStatusList.map((status, index) => ({
        itemId: index + 1,
        statusId: status.statusId ?? 0,
        name: status.name,
        englishName: status.englishName,
        arabicName: status.arabicName,
        isNotify: status.isNotify,
        waTemplateId: status.waTemplateId,
        templateName: status.templateName ?? null,
        isUsed: status.isUsed ?? false,
      })),
    );
  }, [customStatusList]);

  const addNewCustomStatus = (data: IOrderCustomStatusFormData) => {
    const customStatus = { ...data, name: language === 'en' ? data.englishName : data.arabicName };

    append({ ...customStatus, itemId: fields.length + 1 });
    setCustomStatuses((prev) => [...prev, { ...customStatus, itemId: prev.length + 1 }]);
    setShowAddModal(false);
  };

  const editStatusHandler = (data: IOrderCustomStatusFormData) => {
    const customStatus = { ...data, name: language === 'en' ? data.englishName : data.arabicName };

    const index = fields.findIndex((s) => s.itemId === customStatus.itemId);
    if (index !== -1) {
      update(index, {
        ...fields[index],
        ...customStatus,
      });
    }
    setCustomStatuses((prev) => {
      const updated = prev.map((status) => (status.itemId === customStatus.itemId ? { ...status, ...customStatus } : status));
      return updated;
    });
  };

  const deleteStatusHandler = (itemId: number) => {
    remove(fields.findIndex((s) => s.itemId === itemId));
    setCustomStatuses((prev) => {
      const updated = prev.filter((status) => status.itemId !== itemId);
      return updated;
    });
  };
  return (
    <>
      <div className="py-3">
        <div className="d-flex justify-content-between flex-wrap gap-1">
          <FormLabel label={resources.customStatusLabel} labelDesc={resources.customStatusLabelDesc} />
          <div>
            <button className="btn btn-outline-primary" type="button" onClick={() => setShowAddModal(true)}>
              <Plus width={24} height={24} /> {resources.addCustomStatusLabel}
            </button>
          </div>
        </div>

        <div>
          <div className="table-responsive overflow-auto border rounded-12 my-3 w-100" style={{ maxHeight: 500 }}>
            <table className="table table-custom align-middle p-3 my-0">
              <thead>
                <CustomStatusListHeader headerItems={headerItems} truncateWidth={100} hasActions />
              </thead>
              <tbody>
                {isListLoading ? (
                  <TableSkeleton />
                ) : isError ? (
                  <tr>
                    <td colSpan={4} className="border-0 text-center">
                      <Error />
                    </td>
                  </tr>
                ) : customStatuses && customStatuses.length > 0 ? (
                  customStatuses.map((item) => <CustomStatusListItem key={item.itemId} item={item} onEdit={editStatusHandler} onDelete={deleteStatusHandler} />)
                ) : (
                  <tr>
                    <td colSpan={4} className="border-0 text-center">
                      <EmptyState title={resources.noDataTitle} />
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      {showAddModal && <AddCustomStatusModal mode="add" show={showAddModal} onClose={() => setShowAddModal(false)} onSubmit={addNewCustomStatus} />}
    </>
  );
};

export default CustomStatusForm;
